// src/pages/shared/ReadOnlyRounds.jsx
// The rounds as a spectator sees them: who plays whom, and the scores. Nothing can be edited.
import React, { useState } from 'react';
import { ChevronIcon } from '../../components/icons';
import { setsWon } from '../tournament/roundsState';
import { formatExact, formatWhen } from '../../utils/time';
import { groupName, roundTitle } from '../../utils/groups';

function Team({ label, className, team }) {
    const players = team?.players || [];
    return (
        <div className={`team ${className}`}>
            <div className="team-label">{label}</div>
            <ul>
                {players.map((player) => (
                    <li key={player.playerId}>
                        {player.firstName} {player.lastName}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Score({ set }) {
    const leading1 = set.team1Score > set.team2Score;
    const leading2 = set.team2Score > set.team1Score;
    return (
        <>
            <span className={`ro-score team-a${leading1 ? ' leading' : ''}`}>{set.team1Score}</span>
            <span className="score-dash" aria-hidden="true">
                –
            </span>
            <span className={`ro-score team-b${leading2 ? ' leading' : ''}`}>{set.team2Score}</span>
        </>
    );
}

function Matchup({ matchup, index, courtNumber, meId }) {
    const mine = meId !== null && meId !== undefined && [...(matchup.team1?.players || []), ...(matchup.team2?.players || [])].some((player) => player.playerId === meId);
    const matches = matchup.matches || [];
    const scored = matches.filter((match) => (match.sets || []).some((set) => set.team1Score + set.team2Score > 0));
    const cancelled = Boolean(matchup.cancelled);
    return (
        <section className={`matchup${cancelled ? ' cancelled' : ''}${mine ? ' mine' : ''}`} aria-label={`Matchup ${index + 1}`}>
            <div className="matchup-head">
                <span>
                    Matchup {index + 1}
                    {matchup.group != null && <span className="badge">Group {groupName(matchup.group)}</span>}
                    {matchup.label && <span className="badge badge-primary">{matchup.label}</span>}
                    {matchup.isSingles && <span className="badge badge-primary">Singles</span>}
                    {matchup.requested && <span className="badge">Requested</span>}
                    {courtNumber && <span className="badge">Court {courtNumber}</span>}
                    {cancelled && <span className="badge badge-cancelled">Cancelled</span>}
                </span>
            </div>
            <div className="teams">
                <Team label={matchup.isSingles ? 'Player A' : 'Team A'} className="team-a" team={matchup.team1} />
                <span className="vs">VS</span>
                <Team label={matchup.isSingles ? 'Player B' : 'Team B'} className="team-b" team={matchup.team2} />
            </div>
            <div className="matches">
                {cancelled ? null : scored.length === 0 ? (
                    <p className="hint">No score yet.</p>
                ) : (
                    scored.map((match) => {
                        const won = setsWon(match);
                        return (
                            <div key={match.matchId} className="match">
                                <div className="match-head">
                                    <div className="match-title">
                                        {matches.length > 1 ? `Match ${match.matchId + 1}` : 'Result'}
                                    </div>
                                                                            <span className="match-score" title="Sets won">
<span className={`ro-score team-a${won.team1 > won.team2 ? ' leading' : ''}`}>{won.team1}</span>
<span className="score-dash" aria-hidden="true">{' – '}</span>
<span className={`ro-score team-b${won.team2 > won.team1 ? ' leading' : ''}`}>{won.team2}</span>
</span>
                                </div>
                                {match.sets
                                    .filter((set) => set.team1Score + set.team2Score > 0)
                                    .map((set, number) => (
                                        <div key={set.setId} className="set-row">
                                            <span className="set-label">Set {number + 1}</span>
                                            <Score set={set} />
                                            {set.updatedAt && (
                                                <span className="set-updated" title={formatExact(set.updatedAt)}>
                                                    {formatWhen(set.updatedAt)}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        );
                    })
                )}
            </div>
        </section>
    );
}

// newest round first and open; the others fold away
function ReadOnlyRounds({ rounds, courts, meId = null }) {
    const [opened, setOpened] = useState({});
    if (rounds.length === 0) {
        return (
            <div className="empty">
                <strong>No rounds yet</strong>
                <p>The first round will show up here as soon as it is planned.</p>
            </div>
        );
    }
    const ordered = [...rounds].sort((a, b) => b.roundId - a.roundId);
    const newest = ordered[0].roundId;
    const isOpen = (round) => opened[round.roundId] ?? round.roundId === newest;

    return (
        <div className="rounds">
            {ordered.map((round) => {
                const open = isOpen(round);
                const idle = round.idlePlayers || [];
                return (
                    <article key={round.roundId} className="card">
                        <div className="round-head">
                            <button
                                className="section-toggle"
                                aria-expanded={open}
                                aria-label={roundTitle(round)}
                                onClick={() => setOpened((prev) => ({ ...prev, [round.roundId]: !open }))}
                            >
                                <ChevronIcon open={open} />
                                <h3>{roundTitle(round)}</h3>
                                {round.roundId === newest && <span className="badge badge-primary">Latest</span>}
                            </button>
                        </div>
                        {open && (
                            <div className="round-body">
                                {idle.length > 0 && (
                                    <p className="sitting-out">
                                        <span className="label">Sitting out</span>
                                        {idle.map((player) => (
                                            <span key={player.playerId} className="badge">
                                                {player.firstName} {player.lastName}
                                            </span>
                                        ))}
                                    </p>
                                )}
                                {round.matchups.map((matchup, index) => (
                                    <Matchup
                                        key={matchup.matchupId}
                                        matchup={matchup}
                                        index={index}
                                        courtNumber={courts ? index + 1 : null}
                                        meId={meId}
                                    />
                                ))}
                            </div>
                        )}
                    </article>
                );
            })}
        </div>
    );
}

export default ReadOnlyRounds;
